"use client";

import { useEffect, useState } from "react";
import { getVoices, getTones, CreatorVoiceError, type Voice, type Tone } from "@/lib/creatorVoice/api";
import { useJob } from "@/lib/creatorVoice/useJob";
import { inputClass, ErrorBox, JobProgress, SharedFreeTrialNote } from "@/components/creatorVoice/CreatorVoiceUI";
import VoicePickerModal from "@/components/creatorVoice/VoicePickerModal";
import BottomPlayer from "@/components/creatorVoice/BottomPlayer";
import UpgradeToPro from "@/components/platform/UpgradeToPro";
import Spinner from "@/components/Spinner";

const PersonIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
    <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.4 0-8 2.2-8 5v1a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1c0-2.8-3.6-5-8-5Z" />
  </svg>
);

function tierLabel(v: Voice): string {
  if (v.tier === "pro") return "🔒 Pro";
  if (v.is_premium) return "★ Premium";
  return "Free";
}

export default function TtsStudioTool() {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [tones, setTones] = useState<Tone[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [toneId, setToneId] = useState("");
  const [speed, setSpeed] = useState(1.0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const { job, submitting, error, capped, run } = useJob();

  function loadOptions() {
    setLoadError(null);
    setOptionsLoading(true);
    Promise.allSettled([
      getVoices().then((v) => {
        setVoices(v);
        if (v[0]) setVoiceId(v[0].id);
      }),
      getTones().then((t) => {
        setTones(t);
        if (t[0]) setToneId(t[0].id);
      }),
    ]).then((results) => {
      const failed = results.find((r) => r.status === "rejected") as PromiseRejectedResult | undefined;
      if (failed) {
        const e = failed.reason;
        setLoadError(e instanceof CreatorVoiceError ? e.message : "Couldn't load voices/tones.");
      }
      setOptionsLoading(false);
    });
  }

  useEffect(() => {
    loadOptions();
  }, []);

  const selectedVoice = voices.find((v) => v.id === voiceId) ?? null;
  const audioUrl = (job?.result?.audio_url as string) || job?.resultUrl;
  const locked = job?.result?.locked === true;
  const showResult = job?.status === "completed" && Boolean(audioUrl);

  return (
    <div>
      {loadError ? (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
          {loadError}{" "}
          <button type="button" onClick={loadOptions} className="font-semibold underline">
            Retry
          </button>
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Main column */}
        <div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={9}
            placeholder="Type or paste your script here…"
            className={`${inputClass} resize-none`}
          />
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => run("tts", { text: text.trim(), voice_id: voiceId, tone_id: toneId, speed })}
              disabled={submitting || optionsLoading || !text.trim() || !voiceId}
              className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Spinner className="h-4 w-4" />
                  Submitting…
                </>
              ) : (
                "Generate Speech"
              )}
            </button>
          </div>
          <SharedFreeTrialNote />

          {error ? <ErrorBox message={error} /> : null}
          {capped ? (
            <div className="mt-3">
              <UpgradeToPro />
            </div>
          ) : null}
          <JobProgress job={job} />
        </div>

        {/* Settings panel */}
        <div className="space-y-4">
          <div className={`relative ${pickerOpen ? "min-h-[420px]" : ""}`}>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Voice</label>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                disabled={optionsLoading}
                className="mt-2 flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-rose-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-[#0f172a] dark:hover:border-rose-700"
              >
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-fuchsia-500 text-white">
                  {optionsLoading ? <Spinner className="h-4 w-4" /> : PersonIcon}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900 dark:text-white">
                    {optionsLoading ? "Loading voices…" : selectedVoice?.label ?? "Choose a voice…"}
                  </span>
                  {selectedVoice && !optionsLoading ? (
                    <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                      {tierLabel(selectedVoice)} · {selectedVoice.language_label}
                    </span>
                  ) : null}
                </span>
                <span className="flex-shrink-0 text-slate-400">›</span>
              </button>
            </div>

            <div className="mt-4">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Tone</label>
              <select value={toneId} onChange={(e) => setToneId(e.target.value)} disabled={optionsLoading} className={inputClass}>
                {optionsLoading ? (
                  <option>Loading…</option>
                ) : (
                  tones.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            {pickerOpen ? (
              <VoicePickerModal voices={voices} selectedId={voiceId} onSelect={setVoiceId} onClose={() => setPickerOpen(false)} />
            ) : null}
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Speed: {speed.toFixed(2)}x</label>
            <input
              type="range"
              min={0.5}
              max={2}
              step={0.05}
              value={speed}
              onChange={(e) => setSpeed(parseFloat(e.target.value))}
              className="mt-2 w-full accent-rose-500"
            />
          </div>
        </div>
      </div>

      <div className="mt-6">
        {showResult && audioUrl ? (
          <BottomPlayer
            icon={PersonIcon}
            title={locked ? "Preview" : "Generated audio"}
            subtitle={locked ? "Subscribe to Nohaya Pro to unlock the full clip" : selectedVoice?.label}
            src={audioUrl}
            downloadable={!locked}
          />
        ) : null}

        {locked ? (
          <div className="mt-3">
            <UpgradeToPro />
          </div>
        ) : null}
      </div>
    </div>
  );
}
